import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { omit } from 'dpl/shared/utils';
import { subscribeToChildListChanges } from 'dpl/util/dom';
import { getDistance, getCenterPoint } from 'dpl/util/touch';

// HACK: Allow for events to bubble up for Croppie instances
// because Croppie binds everything to window :'(
const CLASS_BLACKLIST = [
  'cr-boundary',
  'cr-overlay',
  'cr-slider',
  'cr-slider-wrap',
  'cr-viewport',
  'croppie-container',
  'SimpleSlider__thumb'
];

export const OPT_OUT_CLASS = 'dpl-fixed-scroll-wrapper-opt-out';

/**
 * The purpose for this component is only to allow its content to be
 * scrollable while making any elements underneath it stay put.
 *
 * This is to mitigate an issue with iTouch devices which, despite the
 * elements scrolling capabilities, delegate scrolling to the body element
 * when it reaches its top or bottom (ie `overscroll: contain` is not in their
 * lexicon.)
 *
 * Implementation taken from: https://stackoverflow.com/a/41601290/3220101
 *
 */

export default class FixedScrollWrapper extends Component {
  static propTypes = {
    className: PropTypes.string,
    setRef: PropTypes.func,
    axis: PropTypes.oneOf(['X', 'Y']),
    enablePinchZoom: PropTypes.bool,
    onPinchZoom: PropTypes.func
  };

  static defaultProps = {
    className: null,
    setRef: () => {},
    axis: 'Y',
    enablePinchZoom: false,
    onPinchZoom: () => {}
  };

  _touchTargetStartPosition = {
    clientX: null,
    clientY: null
  };

  _pinchStartDistance = null;
  _lastPinchScale = 1;

  scrollingEl = createRef();

  optedOutElements = [];

  isTotallyScrolledHorizontally(touchTargetX) {
    const newTouchTargetX =
      touchTargetX - this._touchTargetStartPosition.clientX;

    const { scrollLeft, scrollWidth, clientWidth } = this.scrollingEl.current;

    return (
      // all the way right
      (scrollWidth - scrollLeft <= clientWidth && newTouchTargetX < 0) ||
      // all the way left
      (scrollLeft === 0 && newTouchTargetX > 0)
    );
  }

  isTotallyScrolledVertically(touchTargetY) {
    const newTouchTargetY =
      touchTargetY - this._touchTargetStartPosition.clientY;

    const { scrollTop, scrollHeight, clientHeight } = this.scrollingEl.current;

    return (
      // all the way bottom
      (scrollHeight - scrollTop <= clientHeight && newTouchTargetY < 0) ||
      // all the way top
      (scrollTop === 0 && newTouchTargetY > 0)
    );
  }

  disableRubberBandIfNeeded(e) {
    // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#Problems_and_solutions
    const {
      targetTouches: [event]
    } = e;
    const isTotallyScrolled =
      this.props.axis === 'X'
        ? this.isTotallyScrolledHorizontally(event.clientX)
        : this.isTotallyScrolledVertically(event.clientY);

    if (
      isTotallyScrolled &&
      event.target.nodeName !== 'TEXTAREA' &&
      this.optedOutElements.every(el => !el.contains(event.target))
    ) {
      e.preventDefault();
    }
  }

  handleTouchMove = e => {
    const targetClasses = [...e.target.classList];
    if (CLASS_BLACKLIST.filter(c => targetClasses.includes(c)).length > 0) {
      return;
    }

    // Handle two-finger pinch zoom
    if (e.targetTouches.length === 2 && this.props.enablePinchZoom) {
      e.preventDefault(); // Prevent default pinch zoom behavior
      e.stopPropagation();

      const touch1 = e.targetTouches[0];
      const touch2 = e.targetTouches[1];
      const currentDistance = getDistance(touch1, touch2);

      if (this._pinchStartDistance !== null) {
        const scale = currentDistance / this._pinchStartDistance;
        const scaleDelta = scale / this._lastPinchScale;
        this._lastPinchScale = scale;

        // Amplify the scale delta to match browser's native pinch sensitivity
        // Formula: amplified = 1 + (delta - 1) * multiplier
        // This makes changes more pronounced while keeping 1.0 as no-change point
        const amplificationFactor = 1.5;
        const amplifiedScaleDelta = 1 + (scaleDelta - 1) * amplificationFactor;

        const center = getCenterPoint(touch1, touch2);

        this.props.onPinchZoom(amplifiedScaleDelta, center.x, center.y);
      }

      return;
    }

    if (e.targetTouches.length === 1) {
      // Stopping propagation here so we don't block elements from
      // scrolling when a parent element is already blocking. for instance,
      // MobileMenu blocks to only scroll w/in itself, but it may have children
      // who have overflow-scroll as well.
      //
      // TODO: The issue w this is that there may be other listeners listening
      // to touchmove which this would break
      e.stopPropagation();

      if (e.cancelable) {
        this.disableRubberBandIfNeeded(e);
      }
    }
  };

  handleTouchStart = e => {
    if (e.targetTouches.length === 1) {
      const { clientX, clientY } = e.targetTouches[0];
      this._touchTargetStartPosition = { clientX, clientY };
      // Reset pinch state when switching from pinch to single touch
      this._pinchStartDistance = null;
      this._lastPinchScale = 1;
    } else if (e.targetTouches.length === 2 && this.props.enablePinchZoom) {
      const touch1 = e.targetTouches[0];
      const touch2 = e.targetTouches[1];
      this._pinchStartDistance = getDistance(touch1, touch2);
      this._lastPinchScale = 1;
    }
  };

  handleWheel = e => {
    // Detect trackpad pinch zoom (ctrlKey + wheel event)
    if (e.ctrlKey && this.props.enablePinchZoom) {
      e.preventDefault(); // Prevent browser's default zoom
      e.stopPropagation();

      // deltaY is positive when pinching in (zoom out), negative when pinching out (zoom in)
      // Scale the delta to a reasonable zoom factor
      const scaleDelta = 1 - e.deltaY * 0.01;

      // Get the center point of the pinch (mouse position)
      const center = {
        x: e.clientX,
        y: e.clientY
      };

      this.props.onPinchZoom(scaleDelta, center.x, center.y);
    }
  };

  componentDidMount() {
    // TODO: this is just so tests can pass; probs better to fix w createMockNode
    if (this.scrollingEl.current && this.scrollingEl.current.addEventListener) {
      this.scrollingEl.current.addEventListener(
        'touchstart',
        this.handleTouchStart
      );
      this.scrollingEl.current.addEventListener(
        'touchmove',
        this.handleTouchMove
      );
      // Add wheel event listener for trackpad pinch zoom
      // Use passive: false to allow preventDefault() for pinch zoom
      this.scrollingEl.current.addEventListener('wheel', this.handleWheel, {
        passive: false
      });

      this.props.setRef(this.scrollingEl.current);

      this.optedOutElements = [
        ...document.getElementsByClassName(OPT_OUT_CLASS)
      ];

      this.unsubscribeChildChanges = subscribeToChildListChanges(
        this.scrollingEl.current,
        mutation => {
          mutation.addedNodes.forEach(node => {
            if (node instanceof window.HTMLElement) {
              this.optedOutElements = [
                ...this.optedOutElements,
                ...node.getElementsByClassName(OPT_OUT_CLASS)
              ];
            }
          });
        }
      );
    }
  }

  componentWillUnmount() {
    // TODO: this is just so tests can pass; probs better to fix w createMockNode
    if (
      this.scrollingEl.current &&
      this.scrollingEl.current.removeEventListener
    ) {
      this.scrollingEl.current.removeEventListener(
        'touchstart',
        this.handleTouchStart
      );
      this.scrollingEl.current.removeEventListener(
        'touchmove',
        this.handleTouchMove
      );
      this.scrollingEl.current.removeEventListener('wheel', this.handleWheel);
      this.unsubscribeChildChanges();
    }
  }

  render() {
    const { className, axis, ...props } = this.props;

    return (
      <div
        {...omit(props, ['setRef', 'onPinchZoom', 'enablePinchZoom'])}
        ref={this.scrollingEl}
        className={classnames(className, {
          'overflow-y-auto': axis === 'Y',
          'overflow-x-auto': axis === 'X'
        })}
      />
    );
  }
}
